import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

/**
 * Async thunks for API calls to pipeline debugger backend
 */

// Stage 1: Upload audio files
export const uploadAudioFiles = createAsyncThunk(
  'pipelineDebugger/uploadAudioFiles',
  async ({ caseId, providerId, sessionId, files, source }, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append('case_id', caseId);
      formData.append('provider_id', providerId);
      if (sessionId) formData.append('session_id', sessionId);
      if (source) formData.append('source', source);

      files.forEach((file) => {
        formData.append('files', file);
      });

      const response = await axios.post(
        `${API_BASE_URL}/api/pipeline-debug/audio/upload/`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            'Authorization': `Bearer ${localStorage.getItem('api_token')}`
          }
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Stage 2: Concatenate audio
export const concatenateAudio = createAsyncThunk(
  'pipelineDebugger/concatenateAudio',
  async (sessionId, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/pipeline-debug/audio/concatenate/${sessionId}/`,
        {},
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('api_token')}`
          }
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Stage 3: Submit transcription
export const submitTranscription = createAsyncThunk(
  'pipelineDebugger/submitTranscription',
  async ({ audioUrl, sessionId, caseId, options }, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/pipeline-debug/transcription/submit/`,
        { audio_url: audioUrl, session_id: sessionId, case_id: caseId, options },
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('api_token')}`
          }
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Stage 3: Poll transcription status
export const pollTranscriptionStatus = createAsyncThunk(
  'pipelineDebugger/pollTranscriptionStatus',
  async (transcriptionId, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/pipeline-debug/transcription/status/${transcriptionId}/`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('api_token')}`
          }
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Stage 4: Build prompt
export const buildPrompt = createAsyncThunk(
  'pipelineDebugger/buildPrompt',
  async (params, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/pipeline-debug/prompt/build/`,
        params,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('api_token')}`
          }
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Stage 5: Generate note with LLM
export const generateNote = createAsyncThunk(
  'pipelineDebugger/generateNote',
  async (params, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/pipeline-debug/note/generate/`,
        params,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('api_token')}`
          }
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Stage 6: Convert note formats
export const convertNoteFormats = createAsyncThunk(
  'pipelineDebugger/convertNoteFormats',
  async (noteSections, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/pipeline-debug/note/convert-formats/`,
        { note_sections: noteSections },
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('api_token')}`
          }
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Get complete session state
export const fetchSessionState = createAsyncThunk(
  'pipelineDebugger/fetchSessionState',
  async (sessionId, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/pipeline-debug/sessions/${sessionId}/state/`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('api_token')}`
          }
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// List all sessions
export const fetchSessionsList = createAsyncThunk(
  'pipelineDebugger/fetchSessionsList',
  async ({ statusFilter, caseId, limit } = {}, { rejectWithValue }) => {
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      if (caseId) params.case_id = caseId;
      if (limit) params.limit = limit;

      const response = await axios.get(
        `${API_BASE_URL}/api/pipeline-debug/sessions/`,
        {
          params,
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('api_token')}`
          }
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

/**
 * Redux slice for managing pipeline debugger state
 * Tracks all 6 stages of the visit note generation pipeline
 */
const pipelineDebuggerSlice = createSlice({
  name: 'pipelineDebugger',
  initialState: {
    // Session context
    currentSessionId: null,
    currentCaseId: null,
    currentProviderId: null,
    availableSessions: [],

    // Stage 1: Audio Collection
    audioFiles: [],
    uploadedSegments: [],
    uploadStatus: 'idle', // 'idle' | 'loading' | 'success' | 'error'
    uploadError: null,

    // Stage 2: Audio Concatenation
    concatenatedUrl: null,
    concatenationStatus: 'idle',
    concatenationError: null,
    totalDuration: 0,

    // Stage 3: Transcription
    transcriptionId: null,
    transcriptionStatus: 'idle', // AssemblyAI status
    transcript: null,
    utterances: [],
    transcriptionMetadata: {}, // language, confidence, etc.
    transcriptionError: null,
    isPolling: false,

    // Stage 4: Prompt Building
    builtPrompt: null,
    systemMessage: null,
    templateUsed: null,
    providerPreferences: {},
    promptStatus: 'idle',
    promptError: null,

    // Stage 5: LLM Generation
    noteSections: null,
    generationStatus: 'idle',
    generationError: null,
    generationOptions: {
      model: 'gpt-4o',
      temperature: 0.3,
      max_tokens: 3000
    },

    // Stage 6: Format Conversion
    noteFormats: {
      html: '',
      plain_text: '',
      rtf: '',
      formatted_text: ''
    },
    conversionStatus: 'idle',
    conversionError: null,

    // Execution log
    executionLog: [],

    // Complete session state
    sessionState: null,
    sessionStateStatus: 'idle',
    sessionStateError: null,

    // Global pipeline control
    currentStage: 1, // Which stage is active (1-6)
    autoAdvance: false, // Automatically advance to next stage on success

    // UI state
    expandedStages: [1], // Which stage cards are expanded
    showStateInspector: false,
    showExecutionLog: true,
  },
  reducers: {
    // Session context
    setCurrentSessionId: (state, action) => {
      state.currentSessionId = action.payload;
    },
    setCurrentCaseId: (state, action) => {
      state.currentCaseId = action.payload;
    },
    setCurrentProviderId: (state, action) => {
      state.currentProviderId = action.payload;
    },

    // Audio files
    addAudioFiles: (state, action) => {
      state.audioFiles = [...state.audioFiles, ...action.payload];
    },
    removeAudioFile: (state, action) => {
      state.audioFiles = state.audioFiles.filter((_, index) => index !== action.payload);
    },
    clearAudioFiles: (state) => {
      state.audioFiles = [];
    },

    // Transcription options
    updateTranscriptionOptions: (state, action) => {
      state.transcriptionMetadata = { ...state.transcriptionMetadata, ...action.payload };
    },

    // Generation options
    updateGenerationOptions: (state, action) => {
      state.generationOptions = { ...state.generationOptions, ...action.payload };
    },

    // Execution log
    addLogEntry: (state, action) => {
      state.executionLog.push({
        timestamp: new Date().toISOString(),
        ...action.payload
      });
    },
    clearExecutionLog: (state) => {
      state.executionLog = [];
    },

    // Stage control
    setCurrentStage: (state, action) => {
      state.currentStage = action.payload;
      // Auto-expand the current stage
      if (!state.expandedStages.includes(action.payload)) {
        state.expandedStages.push(action.payload);
      }
    },
    toggleStageExpansion: (state, action) => {
      const stageNum = action.payload;
      if (state.expandedStages.includes(stageNum)) {
        state.expandedStages = state.expandedStages.filter(s => s !== stageNum);
      } else {
        state.expandedStages.push(stageNum);
      }
    },
    setAutoAdvance: (state, action) => {
      state.autoAdvance = action.payload;
    },

    // UI toggles
    toggleStateInspector: (state) => {
      state.showStateInspector = !state.showStateInspector;
    },
    toggleExecutionLog: (state) => {
      state.showExecutionLog = !state.showExecutionLog;
    },

    // Reset entire pipeline
    resetPipeline: (state) => {
      state.audioFiles = [];
      state.uploadedSegments = [];
      state.concatenatedUrl = null;
      state.transcriptionId = null;
      state.transcript = null;
      state.utterances = [];
      state.builtPrompt = null;
      state.systemMessage = null;
      state.noteSections = null;
      state.noteFormats = {
        html: '',
        plain_text: '',
        rtf: '',
        formatted_text: ''
      };
      state.executionLog = [];
      state.currentStage = 1;

      // Reset all statuses
      state.uploadStatus = 'idle';
      state.concatenationStatus = 'idle';
      state.transcriptionStatus = 'idle';
      state.promptStatus = 'idle';
      state.generationStatus = 'idle';
      state.conversionStatus = 'idle';

      // Clear errors
      state.uploadError = null;
      state.concatenationError = null;
      state.transcriptionError = null;
      state.promptError = null;
      state.generationError = null;
      state.conversionError = null;
    },

    // Manual data updates (for tweaking)
    updateTranscript: (state, action) => {
      state.transcript = action.payload;
    },
    updatePrompt: (state, action) => {
      state.builtPrompt = action.payload;
    },
    updateSystemMessage: (state, action) => {
      state.systemMessage = action.payload;
    },
    updateNoteSections: (state, action) => {
      state.noteSections = action.payload;
    },
  },

  extraReducers: (builder) => {
    // Upload audio
    builder
      .addCase(uploadAudioFiles.pending, (state) => {
        state.uploadStatus = 'loading';
        state.uploadError = null;
        state.addLogEntry = {
          stage: 1,
          type: 'info',
          message: 'Uploading audio files...'
        };
      })
      .addCase(uploadAudioFiles.fulfilled, (state, action) => {
        state.uploadStatus = 'success';
        state.uploadedSegments = action.payload.segments;
        state.currentSessionId = action.payload.session_id;
        state.executionLog.push({
          timestamp: new Date().toISOString(),
          stage: 1,
          type: 'success',
          message: `Uploaded ${action.payload.segments_uploaded} segments`,
          data: action.payload
        });

        if (state.autoAdvance) {
          state.currentStage = 2;
        }
      })
      .addCase(uploadAudioFiles.rejected, (state, action) => {
        state.uploadStatus = 'error';
        state.uploadError = action.payload;
        state.executionLog.push({
          timestamp: new Date().toISOString(),
          stage: 1,
          type: 'error',
          message: `Upload failed: ${action.payload?.detail || action.payload}`,
          data: action.payload
        });
      });

    // Concatenate audio
    builder
      .addCase(concatenateAudio.pending, (state) => {
        state.concatenationStatus = 'loading';
        state.concatenationError = null;
        state.executionLog.push({
          timestamp: new Date().toISOString(),
          stage: 2,
          type: 'info',
          message: 'Concatenating audio segments...'
        });
      })
      .addCase(concatenateAudio.fulfilled, (state, action) => {
        state.concatenationStatus = 'success';
        state.concatenatedUrl = action.payload.concatenated_url;
        state.totalDuration = action.payload.total_duration_seconds;
        state.executionLog.push({
          timestamp: new Date().toISOString(),
          stage: 2,
          type: 'success',
          message: `Concatenated ${action.payload.segments_processed} segments (${action.payload.total_duration_seconds}s)`,
          data: action.payload
        });

        if (state.autoAdvance) {
          state.currentStage = 3;
        }
      })
      .addCase(concatenateAudio.rejected, (state, action) => {
        state.concatenationStatus = 'error';
        state.concatenationError = action.payload;
        state.executionLog.push({
          timestamp: new Date().toISOString(),
          stage: 2,
          type: 'error',
          message: `Concatenation failed: ${action.payload?.detail || action.payload}`,
          data: action.payload
        });
      });

    // Submit transcription
    builder
      .addCase(submitTranscription.pending, (state) => {
        state.transcriptionStatus = 'loading';
        state.transcriptionError = null;
        state.executionLog.push({
          timestamp: new Date().toISOString(),
          stage: 3,
          type: 'info',
          message: 'Submitting audio for transcription...'
        });
      })
      .addCase(submitTranscription.fulfilled, (state, action) => {
        state.transcriptionStatus = 'processing';
        state.transcriptionId = action.payload.transcription_id;
        state.isPolling = true;
        state.executionLog.push({
          timestamp: new Date().toISOString(),
          stage: 3,
          type: 'success',
          message: `Transcription job submitted (ID: ${action.payload.transcription_id})`,
          data: action.payload
        });
      })
      .addCase(submitTranscription.rejected, (state, action) => {
        state.transcriptionStatus = 'error';
        state.transcriptionError = action.payload;
        state.executionLog.push({
          timestamp: new Date().toISOString(),
          stage: 3,
          type: 'error',
          message: `Transcription submission failed: ${action.payload?.detail || action.payload}`,
          data: action.payload
        });
      });

    // Poll transcription status
    builder
      .addCase(pollTranscriptionStatus.pending, (state) => {
        // Don't add log entries for each poll to avoid spam
      })
      .addCase(pollTranscriptionStatus.fulfilled, (state, action) => {
        state.transcriptionStatus = action.payload.status;

        if (action.payload.status === 'completed') {
          state.transcript = action.payload.transcript;
          state.utterances = action.payload.utterances || [];
          state.transcriptionMetadata = {
            language_code: action.payload.language_code,
            language_confidence: action.payload.language_confidence,
            audio_duration: action.payload.audio_duration,
            confidence: action.payload.confidence
          };
          state.isPolling = false;
          state.executionLog.push({
            timestamp: new Date().toISOString(),
            stage: 3,
            type: 'success',
            message: `Transcription completed (${action.payload.transcript?.length || 0} chars)`,
            data: action.payload
          });

          if (state.autoAdvance) {
            state.currentStage = 4;
          }
        } else if (action.payload.status === 'error') {
          state.transcriptionError = action.payload;
          state.isPolling = false;
          state.executionLog.push({
            timestamp: new Date().toISOString(),
            stage: 3,
            type: 'error',
            message: `Transcription failed`,
            data: action.payload
          });
        }
      })
      .addCase(pollTranscriptionStatus.rejected, (state, action) => {
        state.isPolling = false;
        state.transcriptionError = action.payload;
        state.executionLog.push({
          timestamp: new Date().toISOString(),
          stage: 3,
          type: 'error',
          message: `Transcription polling failed: ${action.payload?.detail || action.payload}`,
          data: action.payload
        });
      });

    // Build prompt
    builder
      .addCase(buildPrompt.pending, (state) => {
        state.promptStatus = 'loading';
        state.promptError = null;
        state.executionLog.push({
          timestamp: new Date().toISOString(),
          stage: 4,
          type: 'info',
          message: 'Building prompt...'
        });
      })
      .addCase(buildPrompt.fulfilled, (state, action) => {
        state.promptStatus = 'success';
        state.builtPrompt = action.payload.prompt_text;
        state.systemMessage = action.payload.system_message;
        state.templateUsed = action.payload.template_used;
        state.providerPreferences = action.payload.provider_preferences;
        state.executionLog.push({
          timestamp: new Date().toISOString(),
          stage: 4,
          type: 'success',
          message: `Prompt built (${action.payload.prompt_length} chars)`,
          data: action.payload
        });

        if (state.autoAdvance) {
          state.currentStage = 5;
        }
      })
      .addCase(buildPrompt.rejected, (state, action) => {
        state.promptStatus = 'error';
        state.promptError = action.payload;
        state.executionLog.push({
          timestamp: new Date().toISOString(),
          stage: 4,
          type: 'error',
          message: `Prompt building failed: ${action.payload?.detail || action.payload}`,
          data: action.payload
        });
      });

    // Generate note
    builder
      .addCase(generateNote.pending, (state) => {
        state.generationStatus = 'loading';
        state.generationError = null;
        state.executionLog.push({
          timestamp: new Date().toISOString(),
          stage: 5,
          type: 'info',
          message: 'Generating note with LLM...'
        });
      })
      .addCase(generateNote.fulfilled, (state, action) => {
        state.generationStatus = 'success';
        state.noteSections = action.payload.note_sections;
        state.executionLog.push({
          timestamp: new Date().toISOString(),
          stage: 5,
          type: 'success',
          message: `Note generated (${Object.keys(action.payload.note_sections || {}).length} sections)`,
          data: action.payload
        });

        if (state.autoAdvance) {
          state.currentStage = 6;
        }
      })
      .addCase(generateNote.rejected, (state, action) => {
        state.generationStatus = 'error';
        state.generationError = action.payload;
        state.executionLog.push({
          timestamp: new Date().toISOString(),
          stage: 5,
          type: 'error',
          message: `Note generation failed: ${action.payload?.detail || action.payload}`,
          data: action.payload
        });
      });

    // Convert formats
    builder
      .addCase(convertNoteFormats.pending, (state) => {
        state.conversionStatus = 'loading';
        state.conversionError = null;
        state.executionLog.push({
          timestamp: new Date().toISOString(),
          stage: 6,
          type: 'info',
          message: 'Converting note formats...'
        });
      })
      .addCase(convertNoteFormats.fulfilled, (state, action) => {
        state.conversionStatus = 'success';
        state.noteFormats = {
          html: action.payload.html,
          plain_text: action.payload.plain_text,
          rtf: action.payload.rtf,
          formatted_text: action.payload.formatted_text
        };
        state.executionLog.push({
          timestamp: new Date().toISOString(),
          stage: 6,
          type: 'success',
          message: `Formats converted (HTML: ${action.payload.html?.length || 0} chars)`,
          data: action.payload
        });
      })
      .addCase(convertNoteFormats.rejected, (state, action) => {
        state.conversionStatus = 'error';
        state.conversionError = action.payload;
        state.executionLog.push({
          timestamp: new Date().toISOString(),
          stage: 6,
          type: 'error',
          message: `Format conversion failed: ${action.payload?.detail || action.payload}`,
          data: action.payload
        });
      });

    // Fetch session state
    builder
      .addCase(fetchSessionState.pending, (state) => {
        state.sessionStateStatus = 'loading';
        state.sessionStateError = null;
      })
      .addCase(fetchSessionState.fulfilled, (state, action) => {
        state.sessionStateStatus = 'success';
        state.sessionState = action.payload;
      })
      .addCase(fetchSessionState.rejected, (state, action) => {
        state.sessionStateStatus = 'error';
        state.sessionStateError = action.payload;
      });

    // Fetch sessions list
    builder
      .addCase(fetchSessionsList.pending, (state) => {
        // Silent loading
      })
      .addCase(fetchSessionsList.fulfilled, (state, action) => {
        state.availableSessions = action.payload.sessions || [];
      })
      .addCase(fetchSessionsList.rejected, (state, action) => {
        console.error('Failed to fetch sessions:', action.payload);
      });
  }
});

// Export actions
export const {
  setCurrentSessionId,
  setCurrentCaseId,
  setCurrentProviderId,
  addAudioFiles,
  removeAudioFile,
  clearAudioFiles,
  updateTranscriptionOptions,
  updateGenerationOptions,
  addLogEntry,
  clearExecutionLog,
  setCurrentStage,
  toggleStageExpansion,
  setAutoAdvance,
  toggleStateInspector,
  toggleExecutionLog,
  resetPipeline,
  updateTranscript,
  updatePrompt,
  updateSystemMessage,
  updateNoteSections,
} = pipelineDebuggerSlice.actions;

// Selectors
export const selectCurrentStage = (state) => state.pipelineDebugger.currentStage;
export const selectAudioFiles = (state) => state.pipelineDebugger.audioFiles;
export const selectUploadedSegments = (state) => state.pipelineDebugger.uploadedSegments;
export const selectConcatenatedUrl = (state) => state.pipelineDebugger.concatenatedUrl;
export const selectTranscript = (state) => state.pipelineDebugger.transcript;
export const selectUtterances = (state) => state.pipelineDebugger.utterances;
export const selectBuiltPrompt = (state) => state.pipelineDebugger.builtPrompt;
export const selectSystemMessage = (state) => state.pipelineDebugger.systemMessage;
export const selectNoteSections = (state) => state.pipelineDebugger.noteSections;
export const selectNoteFormats = (state) => state.pipelineDebugger.noteFormats;
export const selectExecutionLog = (state) => state.pipelineDebugger.executionLog;
export const selectSessionState = (state) => state.pipelineDebugger.sessionState;
export const selectExpandedStages = (state) => state.pipelineDebugger.expandedStages;
export const selectGenerationOptions = (state) => state.pipelineDebugger.generationOptions;

export default pipelineDebuggerSlice.reducer;
